import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { HelpContent } from '@/data/help-content/types';
import type { RolePermissions } from '@/types/roles.types';

interface Props {
  content: HelpContent;
  permissions: RolePermissions;
  searchQuery: string;
}

function resolveDotPath(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[key];
    return undefined;
  }, obj);
}

function isVisible(permissions: RolePermissions, requiredPermission?: string): boolean {
  if (!requiredPermission) return true;
  return resolveDotPath(permissions as unknown as Record<string, unknown>, requiredPermission) === true;
}

function matchesSearch(text: string, query: string): boolean {
  if (!query) return true;
  return text.toLowerCase().includes(query.toLowerCase());
}

export function FAQTab({ content, permissions, searchQuery }: Props) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const visibleFAQs = content.faq.filter(
    (faq) =>
      isVisible(permissions, faq.requiredPermission) &&
      (matchesSearch(faq.question, searchQuery) || matchesSearch(faq.answer, searchQuery)),
  );

  if (visibleFAQs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-sm text-muted-foreground">
          {searchQuery ? `No results found for "${searchQuery}"` : 'No FAQs available for this page.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {visibleFAQs.map((faq, i) => {
        const isOpen = openIndex === i;
        return (
          <div key={i} className="rounded-lg border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : i)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-accent/40 transition-colors"
              aria-expanded={isOpen}
            >
              <span className="text-sm font-medium">{faq.question}</span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
                  isOpen && 'rotate-180',
                )}
              />
            </button>
            {isOpen && (
              <div className="border-t border-border px-4 py-4 space-y-3">
                <p className="text-sm leading-relaxed text-muted-foreground">{faq.answer}</p>
                {faq.steps && faq.steps.length > 0 && (
                  <ol className="space-y-2 pl-1">
                    {faq.steps.map((step, si) => (
                      <li key={si} className="flex items-start gap-3">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground mt-0.5">
                          {si + 1}
                        </span>
                        <p className="text-sm leading-relaxed">{step}</p>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
